import { useEffect } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { type ENRL } from "@/models.ts";

function parseDate(value?: string) {
  if (!value) return null;

  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const m = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function validateWithinProgramYear(
  value: string | undefined,
  programYear: string | undefined,
  fieldLabel: string
): true | string {
  if (!value || !programYear) return true;

  const match = programYear.match(/^(\d{4})-(\d{4})$/);
  if (!match) return true;

  const startYear = parseInt(match[1], 10);
  const endYear = parseInt(match[2], 10);

  const programYearStart = new Date(startYear, 6, 1, 0, 0, 0, 0);
  const programYearEnd = new Date(endYear, 5, 30, 23, 59, 59, 999);

  const date = parseDate(value);
  if (!date) return true;

  if (date < programYearStart || date > programYearEnd) {
    return `${fieldLabel} must occur within Program Year ${programYear}`;
  }

  return true;
}

export function useENRLFormLogic(form: UseFormReturn<ENRL, any, ENRL>) {
  const {
    control,
    trigger,
    getValues,
    formState: { isSubmitted },
  } = form;

  const enrollmentStartDate = useWatch({
    control,
    name: "EnrollmentStartDate",
  });

  const enrollmentEndDate = useWatch({
    control,
    name: "EnrollmentEndDate",
  });

  useEffect(() => {
    if (!isSubmitted) return;

    trigger([
      "EnrollmentStartDate",
      "EnrollmentEndDate",
      "EnrollmentEndReason",
    ]);
  }, [enrollmentStartDate, enrollmentEndDate, isSubmitted, trigger]);

  const startDateRules = {
    validate: (value?: string) => {
      if (!value) return true;
      return validateWithinProgramYear(
        value,
        getValues("ProgramYear"),
        "Enrollment Start Date"
      );
    },
  };

  const endDateRules = {
    validate: {
      afterStart: (endValue?: string) => {
        if (!endValue) return true;

        const startValue = getValues("EnrollmentStartDate");
        if (!startValue) return true;

        const startDate = parseDate(startValue);
        const endDate = parseDate(endValue);

        if (!startDate || !endDate) return true;

        return endDate.getTime() >= startDate.getTime()
          ? true
          : "Enrollment End Date must be on or after Enrollment Start Date";
      },
      withinProgramYear: (endValue?: string) => {
        if (!endValue) return true;
        return validateWithinProgramYear(
          endValue,
          getValues("ProgramYear"),
          "Enrollment End Date"
        );
      },
    },
  };

  const endReasonRules = {
    validate: (reasonValue?: string) => {
      const endValue = getValues("EnrollmentEndDate");

      if (!endValue) return true;
      return reasonValue && reasonValue.trim() !== ""
        ? true
        : "'Enrollment End Reason' is required and cannot be empty.";
    },
  };

  return {
    startDateRules,
    endDateRules,
    endReasonRules,
  };
}
